"use client";

import clsx from "clsx";
import type { ContractAggregate, LcComposerState } from "@/lib/sse/useSSE";
import { CdmState } from "@/components/cdm/CdmState";
import { Iso20022Ribbon } from "@/components/scenes/shared/Iso20022Ribbon";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 3.1 / 4.14 / 5.13 — Settlement Composer.
 *   Left pane: contract & invoice (Seller-issued) already populated.
 *   Right pane: settlement structure picker — tenor 30/60/120 with identical fee.
 *
 * `lcMode={true}` adds the ISO 20022 message ribbon footer (TSRV / TSMT
 * / pacs message family) — surfaced ONLY when a bank LC is being placed.
 * T1 uses Programmable Escrow (no bank), so the bank-message family
 * (tsrv.001 Undertaking Issuance) doesn't apply — natural teaching moment.
 */
export function LcComposer({
  contract,
  composer,
  lcMode = false,
  showTenor = true,
  tenorAsPackage = false,
  selectedTenorDays,
  className,
}: {
  contract: ContractAggregate | null;
  composer: LcComposerState;
  /** Render the ISO 20022 message ribbon footer (LC scenarios only). */
  lcMode?: boolean;
  /** Render the right-hand tenor-window picker. Off on T1's 3.1 since
   *  Programmable Escrow doesn't expose tenor windows to the buyer. */
  showTenor?: boolean;
  /** When true (T2 4.11), the right pane reframes as an "LC bundle
   *  package" picker: 30 d / 60 d / 90 d windows, fee flat across the
   *  three, 60 d selected (SME default), 90 d locked behind an
   *  "Enterprise · upgrade" overlay. Adds a "Pay with your Orbitra ID"
   *  CTA below. */
  tenorAsPackage?: boolean;
  /** Plain (non-tier) mode: highlight one tenor button as selected. T3's
   *  5.13 picks 90 d to land the "longer-tenor reverse-corridor" beat. */
  selectedTenorDays?: number;
  className?: string;
}) {
  const t = useTranslations("lcComposer");
  const tenors = tenorAsPackage
    ? [30, 60, 90]
    : composer.tenorOptionsDays.length ? composer.tenorOptionsDays : [30, 60, 90];
  return (
    <div className={clsx("flex h-full flex-col gap-3", className)}>
      <div className={clsx("grid flex-1 min-h-0 gap-4", showTenor ? "grid-cols-2" : "grid-cols-1")}>
      <div className="glass flex flex-col p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("prePopulated")}</p>
        <h3 className="scene-h2 mt-1 text-orbitra-paper">{t("contractInvoiceTitle")}</h3>

        <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
          <Field label={t("fieldContract")}     value={contract?.id ?? "ORB-CTR-2026-0042"} mono />
          <Field label={t("fieldInvoice")}      value="INV-2026-0042" mono />
          <Field label={t("fieldBuyer")}        value={t("fieldBuyerT1")} />
          <Field label={t("fieldSeller")}       value={t("fieldSellerT1")} />
          <Field label={t("fieldCommodity")}    value={`${contract?.commodity ?? t("fieldCommodityT1")} · HS ${contract?.hsCode ?? "0207.12"}`} />
          <Field label={t("fieldQuantity")}     value={`${(contract?.quantityKg ?? 28000).toLocaleString()} kg`} />
          <Field label={t("fieldGrossAmount")}  value={`USD ${(contract?.priceUsd ?? 850000).toLocaleString()}`} />
          <Field label={t("fieldInvoiceState")} value="" >
            <CdmState token="EXTERNALLY_VALIDATED" />
          </Field>
        </div>

        <div className="mt-auto rounded-md border border-state-financed/30 bg-state-financed/8 p-3 text-[11px]">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-state-financed">{t("mercatorLabel")}</p>
          <p className="mt-1 text-orbitra-paper">"{t("mercatorQuoteEscrow")}"</p>
        </div>
      </div>

      {showTenor && (() => {
        const packageSelectedDays = tenorAsPackage ? (selectedTenorDays ?? 60) : null;
        const packagePlanLabel =
          packageSelectedDays === 30 ? t("packagePlanFreemium") :
          packageSelectedDays === 90 ? t("packagePlanEnterprise") :
                                       t("packagePlanSme");
        return (
        <div className="glass flex flex-col p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="scene-h2 mt-1 text-orbitra-paper">
                {tenorAsPackage ? t("packageTitle") : t("tenorTitle")}
              </h3>
            </div>
            <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed">
              {tenorAsPackage ? packagePlanLabel : t("orbitraRailsBadge")}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            {tenors.map((days) => {
              if (!tenorAsPackage) {
                const selected = selectedTenorDays === days;
                return (
                  <button
                    key={days}
                    className={clsx(
                      "flex flex-col items-start rounded-md border px-3 py-3 text-start text-[11px]",
                      selected
                        ? "border-state-financed/60 bg-state-financed/12 text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]"
                        : "border-state-financed/30 bg-orbitra-ink/40 hover:border-state-financed/60 hover:bg-state-financed/8",
                    )}
                  >
                    <span className="font-display text-lg font-semibold text-orbitra-paper">{days}d</span>
                    <span className="font-semibold text-orbitra-paper">{t("paybackWindow")}</span>
                    <span className="mt-2 font-mono text-[10px] text-state-financed">{t("feeFlat")}</span>
                    {selected && (
                      <span className="mt-1 font-mono text-[10px] uppercase tracking-wider">{t("selectedTag")}</span>
                    )}
                  </button>
                );
              }
              const tier =
                days === 30 ? t("tierFreemium") :
                days === 60 ? t("tierSme")      :
                              t("tierEnterprise");
              const selected = days === packageSelectedDays;
              const locked   = packageSelectedDays != null && days > packageSelectedDays;
              return (
                <div
                  key={days}
                  className={clsx(
                    "relative flex flex-col items-start rounded-md border px-3 py-3 text-start text-[11px]",
                    selected
                      ? "border-state-financed/60 bg-state-financed/12 text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]"
                      : locked
                        ? "border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-muted opacity-70"
                        : "border-state-financed/30 bg-orbitra-ink/40 text-orbitra-paper",
                  )}
                >
                  <span className="flex w-full items-baseline justify-between">
                    <span className="font-display text-lg font-semibold text-orbitra-paper">{days}d</span>
                    <span className={clsx(
                      "rounded-full px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider",
                      selected
                        ? "border border-state-financed/50 bg-state-financed/15 text-state-financed"
                        : locked
                          ? "border border-orbitra-muted/40 bg-orbitra-muted/15 text-orbitra-muted"
                          : "border border-state-financed/30 bg-orbitra-ink/40 text-state-financed",
                    )}>
                      {tier}
                    </span>
                  </span>
                  <span className="text-orbitra-teal-soft">{t("tenorWindow")}</span>
                  <span className="mt-2 font-mono text-[10px] text-state-financed">{t("feeFlat")}</span>
                  {selected && (
                    <span className="mt-1 font-mono text-[10px] uppercase tracking-wider">{t("selectedTag")}</span>
                  )}
                  {locked && (
                    <button
                      type="button"
                      className="mt-2 w-full rounded-md bg-state-financed px-3 py-1.5 font-display text-[11px] font-semibold uppercase tracking-wider text-orbitra-ink shadow-[0_0_18px_var(--state-financed-glow)] hover:bg-state-financed/90"
                    >
                      {t("upgradeCta")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {tenorAsPackage && (
            <p className="mt-3 text-[10px] text-orbitra-muted">
              {packageSelectedDays === 90 ? t("packageNote90") : t("packageNote60")}
            </p>
          )}

          {tenorAsPackage ? (
            <button
              type="button"
              onClick={advanceScreen}
              className="cta-primary mt-auto w-full py-2 text-[12px] font-semibold"
            >
              {t("packagePayCta")}
            </button>
          ) : (
            <div className="mt-auto pt-4 text-[10px] text-orbitra-muted">
              {t("vendorNote")}
            </div>
          )}
        </div>
        );
      })()}
      </div>

      {lcMode && <Iso20022Ribbon activeIndex={1} />}
    </div>
  );
}

function Field({ label, value, mono, children }: { label: string; value: string; mono?: boolean; children?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      {children ?? <p className={clsx("mt-0.5 text-orbitra-paper", mono && "font-mono")}>{value}</p>}
    </div>
  );
}
